import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import Button from "components/Button";
import ButtonInverted from "components/ButtonInverted";
import FormMetaStep from "../FormMetaStep";
import FormBuilderStep from "../FormBuilderStep";
import { initialFormValues } from "../../../../constants";

const BackDrop = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(56, 57, 61, 0.2);
  z-index: 999;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Container = styled.div`
  background-color: #fefefe;
  width: min(1100px, calc(100vw - 32px));
  max-height: calc(100vh - 40px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #ececec;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderText = styled.h2`
  font-size: 18px;
  color: #38393d;
  margin: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 24px;
  cursor: pointer;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const ProgressStep = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: ${({ active }) => (active ? "#0073ab" : "#707070")};
  font-weight: ${({ active }) => (active ? 700 : 400)};
  cursor: pointer;
`;

const ProgressLine = styled.div`
  width: 48px;
  height: 1px;
  background: #d9d9d9;
  margin: 0 12px;
`;

const BodyContainer = styled.div`
  padding: 24px;
  overflow-y: auto;
`;

const FooterContainer = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #ececec;
`;

function parseScreeningType(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
}

function AddForm({
  setShowModal,
  bffService,
  buildStructure,
  validateUniqueFieldIds,
  validateFormStructure,
  onSuccess,
}) {
  const [formValues, setFormValues] = useState(initialFormValues);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const canMoveNext = useMemo(
    () => Boolean(formValues.name.trim()) && Boolean(formValues.version.trim()),
    [formValues.name, formValues.version]
  );

  const handleSubmit = () => {
    if (!formValues.name.trim() || !formValues.version.trim()) {
      toast.error("Preencha nome e versão para continuar.");
      return;
    }

    const duplicateFieldIdsError = validateUniqueFieldIds(formValues);
    if (duplicateFieldIdsError) {
      toast.error(duplicateFieldIdsError);
      return;
    }

    const invalidStructureError = validateFormStructure(formValues);
    if (invalidStructureError) {
      toast.error(invalidStructureError);
      return;
    }

    setSaving(true);
    bffService
      .createForm({
        name: formValues.name.trim(),
        description: formValues.description.trim() || null,
        version: formValues.version.trim(),
        screeningType: parseScreeningType(formValues.screeningType),
        isCurrent: formValues.isCurrent,
        structure: buildStructure(formValues),
      })
      .then(() => {
        toast.success("Formulário criado com sucesso!");
        onSuccess();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Falha ao criar formulário");
      })
      .finally(() => setSaving(false));
  };

  return (
    <BackDrop>
      <Container>
        <HeaderContainer>
          <HeaderTop>
            <HeaderText>Adicionar Formulário</HeaderText>
            <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
          </HeaderTop>

          <ProgressContainer>
            <ProgressStep active={currentStep === 1} onClick={() => setCurrentStep(1)}>
              1. Metadados
            </ProgressStep>
            <ProgressLine />
            <ProgressStep active={currentStep === 2} onClick={() => canMoveNext && setCurrentStep(2)}>
              2. Construtor
            </ProgressStep>
          </ProgressContainer>
        </HeaderContainer>

        <BodyContainer>
          {currentStep === 1 ? (
            <FormMetaStep formValues={formValues} setFormValues={setFormValues} />
          ) : (
            <FormBuilderStep formValues={formValues} setFormValues={setFormValues} />
          )}
        </BodyContainer>

        <FooterContainer>
          <ButtonInverted label="Cancelar" onClick={() => setShowModal(false)} />
          {currentStep === 2 ? (
            <ButtonInverted label="Voltar" onClick={() => setCurrentStep(1)} />
          ) : null}
          {currentStep === 1 ? (
            <Button label="Avançar" onClick={() => setCurrentStep(2)} disabled={!canMoveNext} />
          ) : (
            <Button label={saving ? "Salvando..." : "Concluir"} onClick={handleSubmit} disabled={saving} />
          )}
        </FooterContainer>
      </Container>
    </BackDrop>
  );
}

export default AddForm;
